import { FC, useState } from 'react';
import { UPLOAD_SUCCESS_MESSAGE } from './constants';
import { formatDate, mapUpdateAccountInput } from '../Account';
import { useForm, useGetAccount, useUpdateAccount } from '@/data';
import { AccountPhoto, Alert, Container, Heading, useAuth } from '@/components';
import { useNotify } from '@/hooks';

import { omit } from 'lodash';

import styles from './UploadMedicalDocument.module.scss';

interface IUploadMedicalDocumentProp {
  email: string;
  onUploadSuccess?: () => void;
}

const title =
  process.env.NEXT_PUBLIC_MEDICAL_DOCUMENT_TITLE || 'Upload Medical Document';
const description =
  process.env.NEXT_PUBLIC_MEDICAL_DOCUMENT_DESCRIPTION ||
  // eslint-disable-next-line max-len
  'Please upload a clear photo of your medical ID. Ensure the document is not expired and the photo is taken against a solid background for better clarity.';

const UploadMedicalDocument: FC<IUploadMedicalDocumentProp> = ({
  email,
  onUploadSuccess,
}) => {
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const { updateAccount } = useUpdateAccount();
  const { inputs, setInputs } = useForm();
  const { isAuth, goToCheckout } = useAuth();
  const { notify } = useNotify();

  const [feedback, setFeedback] = useState<{
    error?: boolean;
    isOpen?: boolean;
    message?: string;
  }>({});

  const { loading: isGettingAccount } = useGetAccount({
    onCompleted: res => {
      setInputs({
        ...(omit(res.getAccountByEmail, [
          'medical_id',
          'medical_document_photo_uri',
        ]) ?? {}),
        medical_id: res.getAccountByEmail?.medical_id?.number ?? '',
        medical_id_expiration_date: formatDate(
          res.getAccountByEmail?.medical_id?.expiration_date,
        ),
        medical_id_photo:
          res.getAccountByEmail?.medical_document_photo_uri ?? '',
      });
    },
    fetchPolicy: 'network-only',
    skip: !isAuth,
    variables: {
      email,
    },
  });

  const showError = error => {
    setFeedback({
      error: true,
      isOpen: true,
      message:
        error.message ?? 'Account information not updated. Please, try again.',
    });

    // eslint-disable-next-line no-console
    console.error(error);
  };

  const updateMedicalDocumentAndGoToCheckout = async (message?: string) => {
    setIsUploadingDocument(true);

    if (message) {
      notify('success', message);
    }

    setIsUploadingDocument(false);

    if (onUploadSuccess) {
      onUploadSuccess();
    } else {
      goToCheckout();
    }
  };

  const handleImageChange = (field, url, error) => {
    if (error) {
      showError({ message: error });
      return;
    }

    setIsUploadingDocument(true);

    const input = mapUpdateAccountInput({ ...inputs, [field]: url });

    updateAccount({
      variables: {
        input,
      },
    })
      .then(() => {
        updateMedicalDocumentAndGoToCheckout(UPLOAD_SUCCESS_MESSAGE);
      })
      .catch(e => showError({ message: e }))
      .finally(() => setIsUploadingDocument(false));
  };

  if (inputs?.medical_id_photo) {
    updateMedicalDocumentAndGoToCheckout();

    return null;
  }

  const isLoading = isGettingAccount || isUploadingDocument;

  return (
    <Container className={styles.medical__container}>
      <div className={styles.medical__container_body}>
        <Heading level={1}>{title}</Heading>
        <p>{description}</p>
        <form>
          <Alert
            error={!!feedback.error}
            isOpen={!!feedback.isOpen}
            toggle={() => setFeedback({})}
          >
            {feedback.message}
          </Alert>
          <AccountPhoto
            alt="Medical ID picture"
            field="medical_id_photo"
            loading={isLoading}
            onChange={handleImageChange}
            title="Upload a photo of your medical ID."
            value={inputs.medical_id_photo}
          />
        </form>
      </div>
    </Container>
  );
};

export { UploadMedicalDocument };
